"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Lock,
  User,
  AlertCircle,
  Loader2,
  UserPlus,
  Eye,
  EyeOff,
  Mail,
  ArrowRight,
  RefreshCw
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";

export default function LoginPage() {
  const router = useRouter();
  const { login, isLoading: authLoading } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isInternalLoading, setIsInternalLoading] = useState(false);

  // הוספנו סטייט ששומר האם 2FA פעיל בשרת (ברירת מחדל: מופעל כדי להיות בטוחים)
  const [is2FAEnabled, setIs2FAEnabled] = useState(true);

  // טיימר לשליחה חוזרת (למנוע ספאם)
  const [resendTimer, setResendTimer] = useState(0);

  // בדיקת סטטוס 2FA מול השרת ברגע שהדף עולה
  useEffect(() => {
    fetch("https://api.tuviai.org/admin/global-2fa-status")
      .then((res) => res.json())
      .then((data) => {
        if (data && typeof data.is_2fa_enabled !== "undefined") {
          setIs2FAEnabled(data.is_2fa_enabled);
        }
      })
      .catch((err) => console.error("שגיאה במשיכת סטטוס 2FA", err));
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const getErrorMessage = (err: any) => {
    if (typeof err === "string") return err;
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    if (err.message) return err.message;
    return "שגיאה לא ידועה. עבור.";
  };

  // פונקציית שליחת הקוד
  const sendCode = async (isResend = false) => {
    setError("");
    if (!isResend) setIsInternalLoading(true);

    try {
      const response = await fetch("https://api.tuviai.org/auth/send-2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username }),
      });

      const data = await response.json();

      if (response.ok) {
        if (!isResend) setStep(2);
        setSuccessMsg(isResend ? "קוד חדש נשלח למייל שלך. עבור." : "");
        setResendTimer(60); // חסימה לדקה
      } else {
        setError(getErrorMessage(data));
      }
    } catch (err: any) {
      setError("אין תקשורת עם השרת. וודא שחיבור האינטרנט תקין.");
    } finally {
      if (!isResend) setIsInternalLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setError("");

    if (step === 1) {
      if (!username || !password) {
        setError("נא למלא את כל השדות");
        return;
      }

      // כאן הקסם: אם 2FA כבוי במערכת, מדלגים על שליחת הקוד ומתחברים ישירות!
      if (!is2FAEnabled) {
        setIsInternalLoading(true);
        try {
          // שולחים מחרוזת ריקה כקוד אימות כי הוא לא נדרש
          const success = await login(username, password, "");
          if (success) {
            router.push("/");
          } else {
            setError("פרטי ההתחברות שגויים.");
          }
        } catch (err: any) {
          setError(getErrorMessage(err));
        } finally {
          setIsInternalLoading(false);
        }
      } else {
        // אם 2FA מופעל - ממשיכים כרגיל לשלב 2
        await sendCode();
      }
    } else {
      if (!twoFactorCode || twoFactorCode.length < 6) {
        setError("נא להזין קוד בן 6 ספרות");
        return;
      }

      setIsInternalLoading(true);
      try {
        const success = await login(username, password, twoFactorCode);
        if (success) {
          router.push("/");
        } else {
          setError("פרטי ההתחברות או הקוד שגויים.");
        }
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setIsInternalLoading(false);
      }
    }
  };

  const combinedLoading = authLoading || isInternalLoading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] p-4" dir="rtl">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex flex-col items-center text-center">
          <img
            src="/apple-icon.png"
            alt="טוביה"
            className="mb-5 h-20 w-20 rounded-[22px] shadow-2xl shadow-blue-900/20 object-cover border border-slate-100"
          />
          <h1 className="text-2xl font-black text-slate-900 leading-tight">טוביה AI</h1>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">עוזר לוגיסטי חכם </p>
<span className="bg-blue-50 text-blue-600 text-[10px] font-black px-2 py-0.5 rounded-md border border-blue-100 tracking-tighter">
      Beta v1.0
    </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {step === 1 ? (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-bold text-slate-700 pr-1 text-sm">שם משתמש</Label>
                <div className="relative group">
                  <User className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-900 transition-colors" />
                  <Input
                    id="username"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-14 rounded-2xl pr-10 border-slate-200 focus-visible:ring-blue-900 font-medium"
                    placeholder="הזן שם משתמש"
                    disabled={combinedLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <Label htmlFor="password" className="font-bold text-slate-700 text-sm">סיסמה</Label>
                  <Link href="/forgot-password" className="text-[11px] font-bold text-blue-900 hover:underline">שכחתי סיסמה?</Link>
                </div>
                <div className="relative group">
                  <Lock className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-900 transition-colors" />
                  <Input
                    id="password"
                    autoComplete="current-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-14 rounded-2xl pr-10 pl-12 border-slate-200 focus-visible:ring-blue-900 font-medium"
                    placeholder="הזן סיסמה"
                    disabled={combinedLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 p-1"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6 animate-in fade-in zoom-in-95 text-center">
              <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-900" />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-black text-slate-900">אימות דו-שלבי</h2>
                <p className="text-xs text-slate-500 font-medium px-4">שלחנו קוד אימות למייל שלך. הזן אותו כדי להמשיך. עבור.</p>
              </div>
              <div className="space-y-3">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ""))}
                  className="h-16 text-center text-2xl font-black tracking-[0.5em] rounded-2xl border-2 border-blue-100 focus:border-blue-900"
                  placeholder="000000"
                  autoFocus
                  disabled={combinedLoading}
                />

                {/* כפתור שלח שוב */}
                <button
                  type="button"
                  onClick={() => sendCode(true)}
                  disabled={combinedLoading || resendTimer > 0}
                  className="flex items-center justify-center gap-1.5 mx-auto text-[11px] font-bold text-blue-900 disabled:text-slate-400 transition-colors"
                >
                  <RefreshCw className={`h-3 w-3 ${resendTimer > 0 ? '' : 'animate-hover'}`} />
                  {resendTimer > 0 ? `ניתן לשלוח שוב בעוד ${resendTimer} שניות` : "לא קיבלת קוד? שלח שוב"}
                </button>
              </div>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs font-bold text-slate-400 hover:text-blue-900 flex items-center justify-center gap-1 mx-auto"
              >
                <ArrowRight className="h-3 w-3" /> חזרה להזנת פרטים
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-2xl bg-red-50 p-4 text-red-600 text-sm font-bold border border-red-100 animate-in fade-in zoom-in-95">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMsg && !error && (
            <div className="rounded-2xl bg-green-50 p-3 text-green-700 text-[11px] font-bold border border-green-100 animate-in fade-in slide-in-from-top-2">
              {successMsg}
            </div>
          )}

          <Button
            type="submit"
            className="h-14 w-full rounded-2xl bg-blue-900 hover:bg-blue-800 text-white text-lg font-black shadow-lg shadow-blue-900/20 transition-all active:scale-95"
            disabled={combinedLoading}
          >
            {combinedLoading ? (
              <><Loader2 className="ml-2 h-5 w-5 animate-spin" /> מעבד...</>
            ) : (
              step === 1 ? (is2FAEnabled ? "המשך לאימות" : "כניסה למערכת") : "כניסה למערכת"
            )}
          </Button>
        </form>

        {step === 1 && (
          <>
            <div className="mt-10 mb-6 relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-[#F8FAFC] px-3 text-slate-400 font-bold">או</span></div>
            </div>

            <Button asChild variant="outline" className="h-14 w-full rounded-2xl border-2 border-dashed border-slate-200 hover:border-blue-900 text-slate-600 font-bold transition-all">
              <Link href="/signup" className="flex items-center justify-center gap-2">
                <UserPlus className="h-5 w-5" />
                <span>לחצו כאן להרשמה ראשונית</span>
              </Link>
            </Button>
          </>
        )}

        <div className="mt-10 text-center">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
             {new Date().getFullYear()}© את"ל חוליית ERP | מערכת מאובטחת
          </p>
        </div>
      </div>
    </div>
  );
}